import { useEffect, useRef } from 'react';
import { invoiceService } from '../services/invoiceService';
import { ReminderItem, ReminderListResponse } from '../types/reminder';

const POLL_INTERVAL_MS = 60_000;
const SEEN_REMINDER_STORAGE_KEY = 'seenDueReminderIds';

const getReminderItems = (response?: ReminderListResponse): ReminderItem[] => {
    const data = response?.data;
    const items = data?.data || data?.items || data?.reminders || response?.items || response?.reminders || [];
    return Array.isArray(items) ? items : [];
};

const getReminderId = (item: ReminderItem): string => {
    return String(
        item._id ||
        item.id ||
        item.invoiceId ||
        `${item.invoiceNumber || ''}-${item.dueDate || ''}-${item.title || item.message || ''}`
    );
};

const buildNotificationBody = (item: ReminderItem): string => {
    if (item.message) return String(item.message);

    const invoiceText = item.invoiceNumber ? `Invoice ${item.invoiceNumber}` : 'Invoice reminder';
    const clientText = item.clientName ? ` for ${item.clientName}` : '';
    const dueText = item.dueDate ? ` is due on ${new Date(item.dueDate).toLocaleDateString()}` : ' is due';
    return `${invoiceText}${clientText}${dueText}`;
};

const loadSeenIds = (): Set<string> => {
    try {
        const raw = localStorage.getItem(SEEN_REMINDER_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
        return new Set();
    }
};

const persistSeenIds = (ids: Set<string>) => {
    try {
        localStorage.setItem(SEEN_REMINDER_STORAGE_KEY, JSON.stringify(Array.from(ids)));
    } catch {
        // Ignore storage issues in restricted browsers.
    }
};

export const useReminderNotifications = (enabled: boolean, navigate?: (path: string) => void) => {
    const seenIdsRef = useRef<Set<string>>(loadSeenIds());

    useEffect(() => {
        if (!enabled) return;
        if (typeof window === 'undefined' || !('Notification' in window)) return;

        if (Notification.permission === 'default') {
            Notification.requestPermission().catch(() => {
                // Ignore permission prompt errors and continue polling silently.
            });
        }

        const pollDueReminders = async () => {
            try {
                if (Notification.permission !== 'granted') return;

                const response = await invoiceService.getDueReminders();
                const dueItems = getReminderItems(response);

                dueItems.forEach((item) => {
                    const id = getReminderId(item);
                    if (!id || seenIdsRef.current.has(id)) return;

                    const title = item.title || 'Due Reminder';
                    const notification = new Notification(title, { body: buildNotificationBody(item) });
                    if (item.invoiceId) {
                        notification.onclick = () => {
                            window.focus();
                            navigate?.(`/invoices/view/${item.invoiceId}`);
                            notification.close();
                        };
                    }
                    seenIdsRef.current.add(id);
                });

                persistSeenIds(seenIdsRef.current);
            } catch {
                // Keep polling even if one call fails.
            }
        };

        pollDueReminders();
        const intervalId = window.setInterval(pollDueReminders, POLL_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [enabled]);
};
